import type { RaceLike } from '@/core/RaceLike';
import type { Track } from '@/world/Track';

/**
 * RaceRecorder.ts (M16) — always-on, lightweight race telemetry, kept on the
 * device. The owner asked "what can I give you to learn from how I race?" —
 * the answer is THIS: it quietly records his real laps so the next tuning
 * session can read them (via the preview console / localStorage) and shape
 * the AI against his actual pace profile instead of guesses.
 *
 * What one trace holds:
 *  - `samples` @4 Hz: [raceTime, playerBallArc(m), carSpeed, carLateral] —
 *    enough to rebuild his speed-vs-curvature profile, punt cadence (ball-arc
 *    jumps), corner entry speeds and line choice;
 *  - `rivalArcs` @1 Hz: every rival's ball arc — per-segment deltas show
 *    exactly WHERE on a course the AI loses time to him;
 *  - `events`: item collects/uses with timestamps.
 *
 * Storage: the last KEEP races in one localStorage key (~25 kB per race).
 * Reading it back: JSON.parse(localStorage.getItem('dribble-racer.traces.v1')).
 */

export interface RaceTrace {
  v: 1;
  city: string;
  difficulty: string;
  field: number;
  startedAt: string;
  finished: boolean;
  playerTimeMs: number | null;
  playerPlace: number | null;
  samples: number[][];
  rivalArcs: number[][];
  events: (string | number)[][];
}

const KEY = 'dribble-racer.traces.v1';
const KEEP = 6; // most-recent races kept
const SAMPLE_DT = 0.25; // player samples at 4 Hz
const RIVAL_DT = 1.0; // rival ball arcs at 1 Hz

export class RaceRecorder {
  private trace: RaceTrace | null = null;
  private lastSample = -1;
  private lastRival = -1;
  private prevHeld: string | null = null;
  private prevActive: string | null = null;

  /** Start recording (a race is beginning). Replaces any unfinished trace. */
  begin(city: string, difficulty: string, field: number): void {
    this.trace = {
      v: 1,
      city,
      difficulty,
      field,
      startedAt: new Date().toISOString(),
      finished: false,
      playerTimeMs: null,
      playerPlace: null,
      samples: [],
      rivalArcs: [],
      events: [],
    };
    this.lastSample = -1;
    this.lastRival = -1;
    this.prevHeld = null;
    this.prevActive = null;
  }

  /** Called every sim tick while racing — cheap (sampling is time-gated). */
  tick(race: RaceLike, track: Track): void {
    const tr = this.trace;
    if (!tr || race.state !== 'racing') return;
    const t = race.raceTime;
    const player = race.player;

    if (t - this.lastSample >= SAMPLE_DT) {
      this.lastSample = t;
      const proj = track.project(player.car.state.x, player.car.state.z, player.car.hint);
      tr.samples.push([
        +t.toFixed(2),
        Math.round(proj.distance > 0 ? player.ballDistance(track) : 0),
        +player.car.speed.toFixed(1),
        +proj.lateral.toFixed(1),
      ]);
    }
    if (t - this.lastRival >= RIVAL_DT) {
      this.lastRival = t;
      tr.rivalArcs.push([
        +t.toFixed(1),
        ...race.racers.filter((r) => !r.isPlayer).map((r) => Math.round(r.ballDistance(track))),
      ]);
    }
    // Item events from the player's slot transitions.
    const pw = player.powerups;
    if (pw.held !== this.prevHeld) {
      if (pw.held) tr.events.push([+t.toFixed(2), 'collect', pw.held]);
      this.prevHeld = pw.held;
    }
    if (pw.activeKind !== this.prevActive) {
      if (pw.activeKind) tr.events.push([+t.toFixed(2), 'use', pw.activeKind]);
      this.prevActive = pw.activeKind;
    }
  }

  /** The race finished — stamp the result and persist. */
  complete(playerPlace: number, playerTimeMs: number | null): void {
    if (!this.trace) return;
    this.trace.finished = true;
    this.trace.playerPlace = playerPlace;
    this.trace.playerTimeMs = playerTimeMs;
    this.persist();
    this.trace = null;
  }

  /** Quit / city change mid-race — keep traces with real driving in them. */
  abandon(): void {
    if (this.trace && this.trace.samples.length > 40) this.persist();
    this.trace = null;
  }

  private persist(): void {
    try {
      const raw = window.localStorage.getItem(KEY);
      const arr: RaceTrace[] = raw ? (JSON.parse(raw) as RaceTrace[]) : [];
      arr.push(this.trace as RaceTrace);
      while (arr.length > KEEP) arr.shift();
      window.localStorage.setItem(KEY, JSON.stringify(arr));
    } catch {
      // Storage full/blocked — telemetry is a bonus, never a blocker.
    }
  }
}
